import React, { useEffect, useRef } from 'react';

import { CUST_GEN_DOT_FILL } from '../../../../theme/customizer-tokens';
import { Canvas } from './DotField.styles';

/*
 * Dot-field tuning. These are pure canvas-render parameters (not CSS), so they
 * live with the draw logic. Three soft blooms drift across a dense dot lattice;
 * each dot's radius AND opacity ramps with the field, so dots swell where the
 * energy passes and shrink to nothing at the edges — an elegant, fluid AI grid.
 */
const SPACING = 12; // px between dots
const DOT_MIN = 0.1; // radius where the field is weakest
const DOT_MAX = 1.7; // radius at the bloom core
const TIME_SCALE = 0.0011; // master clock (per ms) — slow + calm
const STATIC_T = 4.2; // the frame rendered under prefers-reduced-motion
const MAX_DPR = 2;

/** Field-shape defaults — the calm, near-uniform lattice used by the logo tiles. */
const ALPHA_MAX = 0.45; // opacity at the bloom core
const ALPHA_FLOOR = 0.0; // a faint baseline so the grid is ALWAYS present, border-to-border
const GAMMA = 0.55; // field contrast (softer falloff → more of the lattice lit)
const BLOOM_SCALE = 0.75; // bloom radius multiplier (1 = the broad default blooms)

/** Per-instance overrides for the field's shape. Defaults give the logo tiles'
 *  calm, near-uniform lattice; the deploy preview turns these up for a gappier
 *  field where the energy reads as it flows (dark space between the blooms). */
export interface DotFieldTuning {
  /** Opacity at a bloom core (the brightest dots). Default 0.45. */
  alphaMax?: number;
  /** Baseline opacity for un-energised dots — LOWER = darker gaps between the
   *  blooms (more negative space as the field flows). Default 0.07. */
  alphaFloor?: number;
  /** Field contrast. HIGHER = sharper falloff = the blooms read as distinct lit
   *  regions with darker gaps around them. Default 0.55. */
  gamma?: number;
  /** Bloom radius multiplier (<1 = tighter blooms → more dark space). Default 1. */
  bloomScale?: number;
}

export interface DotFieldProps {
  /** Seed so a set of fields never drifts in lockstep (each bloom takes a distinct
   *  path + phase). */
  seed: number;
  /** Optional field-shape overrides (see {@link DotFieldTuning}). */
  tuning?: DotFieldTuning;
  /** Dot ink. Defaults to the cool near-white for the dark plate; pass the light
   *  counterpart ({@link CUST_GEN_DOT_FILL_LIGHT}) when the field overlays a card
   *  that has swapped to its light plate, so the dots don't vanish into it. */
  fill?: string;
  className?: string;
}

/**
 * The animated monochrome dot field — a `<canvas>` of dots whose size + brightness
 * is driven by a soft field of three blooms drifting on slow Lissajous paths.
 * Fills its (positioned) parent. Used as the "generating" placeholder texture AND
 * as the dissolving overlay a logo emerges from. Decorative.
 */
export const DotField = ({ seed: seedInput, tuning, fill, className }: DotFieldProps): React.ReactElement => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Resolve the field shape to primitives so the draw closure (and the effect's
  // deps) react to a tuning change without restarting on every render.
  const alphaMax = tuning?.alphaMax ?? ALPHA_MAX;
  const alphaFloor = tuning?.alphaFloor ?? ALPHA_FLOOR;
  const gamma = tuning?.gamma ?? GAMMA;
  const bloomScale = tuning?.bloomScale ?? BLOOM_SCALE;
  const dotFill = fill ?? CUST_GEN_DOT_FILL;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    // Deterministic per-seed phase + a slight speed jitter so neighbours never sync.
    const seed = seedInput * 1.618;
    const speed = 1 + (seedInput % 3) * 0.06;

    let w = 1;
    let h = 1;
    let dpr = 1;

    const resize = (): void => {
      const rect = canvas.getBoundingClientRect();
      w = Math.max(1, rect.width);
      h = Math.max(1, rect.height);
      dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    };

    const draw = (t: number): void => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = dotFill;

      const minWH = Math.min(w, h);
      // Three soft blooms drifting on slow, non-harmonic Lissajous paths, each a
      // different weight + breathing radius, so the combined field reads as a
      // flowing whole rather than discrete spotlights.
      const c1x = (0.5 + 0.3 * Math.sin(t * 0.62 + seed)) * w;
      const c1y = (0.5 + 0.32 * Math.sin(t * 0.81 + seed * 1.7)) * h;
      const c2x = (0.5 + 0.34 * Math.sin(t * 0.47 + seed * 2.3 + 1.1)) * w;
      const c2y = (0.5 + 0.27 * Math.sin(t * 0.73 + seed * 0.9 + 2.4)) * h;
      const c3x = (0.5 + 0.28 * Math.sin(t * 0.55 + seed * 1.3 + 3.4)) * w;
      const c3y = (0.5 + 0.33 * Math.sin(t * 0.4 + seed * 2.1 + 0.6)) * h;
      const s1 = minWH * (0.5 + 0.06 * Math.sin(t * 0.5 + seed)) * bloomScale; // gently breathing
      const s2 = minWH * 0.34 * bloomScale;
      const s3 = minWH * (0.42 + 0.05 * Math.sin(t * 0.43 + seed * 1.9)) * bloomScale;
      const inv1 = 1 / (2 * s1 * s1);
      const inv2 = 1 / (2 * s2 * s2);
      const inv3 = 1 / (2 * s3 * s3);

      // Overdraw one cell past each edge so the lattice reaches the card borders
      // (the card clips the overflow) — the grid is never inset.
      const cols = Math.ceil(w / SPACING) + 1;
      const rows = Math.ceil(h / SPACING) + 1;
      const ox = (w - (cols - 1) * SPACING) / 2;
      const oy = (h - (rows - 1) * SPACING) / 2;

      for (let iy = 0; iy < rows; iy++) {
        const py = oy + iy * SPACING;
        for (let ix = 0; ix < cols; ix++) {
          const px = ox + ix * SPACING;
          const d1 = (px - c1x) ** 2 + (py - c1y) ** 2;
          const d2 = (px - c2x) ** 2 + (py - c2y) ** 2;
          const d3 = (px - c3x) ** 2 + (py - c3y) ** 2;
          let v = Math.exp(-d1 * inv1) + 0.7 * Math.exp(-d2 * inv2) + 0.6 * Math.exp(-d3 * inv3);
          // A faint plasma so dots shimmer individually at the bloom's edge.
          v += 0.05 * Math.sin(px * 0.05 + t * 1.3) * Math.sin(py * 0.05 - t * 1.1);
          // Clamp (never skip) so EVERY cell draws a dot — a faint floor everywhere,
          // the bloom brightening it from there.
          v = v < 0 ? 0 : v > 1 ? 1 : v;
          v **= gamma;
          ctx.globalAlpha = alphaFloor + (alphaMax - alphaFloor) * v;
          ctx.beginPath();
          ctx.arc(px, py, DOT_MIN + (DOT_MAX - DOT_MIN) * v, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
    };

    let raf = 0;
    let frame = 0;
    const loop = (now: number): void => {
      // Re-measure ~4×/sec so the field tracks container resizes AND ancestor
      // transform/scale changes — the deploy preview renders this inside a
      // `transform: scale()` device screen whose VISUAL size a ResizeObserver on
      // the canvas's own (unscaled) box never sees. Cheap: one getBoundingClientRect
      // inside rAF (no forced reflow), and redrawing the same `t` can't flicker.
      if ((frame++ & 15) === 0) resize();
      draw(now * TIME_SCALE * speed);
      raf = window.requestAnimationFrame(loop);
    };

    resize();
    const ro = new ResizeObserver(() => {
      resize();
      if (reduce) draw(STATIC_T);
    });
    ro.observe(canvas);

    if (reduce) {
      draw(STATIC_T);
    } else {
      raf = window.requestAnimationFrame(loop);
    }

    return () => {
      window.cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [seedInput, alphaMax, alphaFloor, gamma, bloomScale, dotFill]);

  return <Canvas ref={canvasRef} className={className} />;
};

export default DotField;
